import React, { useMemo, useState } from 'react'
import { LinkButton, type LinkButtonSize } from '@enamelai/design-system'
import {
  LINK_BUTTON_PREVIEW_STATE_LABELS,
  LINK_BUTTON_PREVIEW_STATES,
  LINK_BUTTON_SIZE_LABELS,
  LINK_BUTTON_SIZES,
  LinkButtonPreviewState,
} from '../showcaseConstants'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './LinkButtonShowcase.css'

const LinkButtonShowcase: React.FC = () => {
  const [label, setLabel] = useState('View details')
  const [size, setSize] = useState<LinkButtonSize>('md')
  const [underline, setUnderline] = useState(true)
  const [disabled, setDisabled] = useState(false)
  const [highlightedState, setHighlightedState] =
    useState<LinkButtonPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'link-button-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="link-button-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as LinkButtonPreviewState,
                  )
                }
              >
                {LINK_BUTTON_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {LINK_BUTTON_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'link-button-label',
            label: 'Label',
            control: (
              <input
                id="link-button-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'link-button-size',
            label: 'Size',
            control: (
              <select
                id="link-button-size"
                className="ui-field-control"
                value={size}
                onChange={(event) =>
                  setSize(event.target.value as LinkButtonSize)
                }
              >
                {LINK_BUTTON_SIZES.map((option) => (
                  <option key={option} value={option}>
                    {LINK_BUTTON_SIZE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'link-button-underline',
            label: 'Underline',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="link-button-underline"
                  type="checkbox"
                  checked={underline}
                  onChange={(event) => setUnderline(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'link-button-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="link-button-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [disabled, highlightedState, label, size, underline],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Link button"
        description="Plain-text action styled like a hyperlink for low-emphasis inline actions."
        stageClassName="ui-preview__stage--wide link-button-showcase__stage-wrap"
      >
        <div className={`link-button-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Plain-text hyperlink actions for low-emphasis inline CTAs.
            </p>
            <div className={`link-button-showcase__live`}>
              <LinkButton size={size} underline={underline} disabled={disabled}>
                {label}
              </LinkButton>
            </div>
          </section>

          <section className="link-button-showcase__matrix">
            <h3 className="ui-showcase-live__title">Sizes</h3>
            <div className={`link-button-showcase__row`}>
              <div className="link-button-showcase__size-row">
                {LINK_BUTTON_SIZES.map((option) => (
                  <div key={option} className="link-button-showcase__size-item">
                    <span className="link-button-showcase__label">
                      {LINK_BUTTON_SIZE_LABELS[option]}
                    </span>
                    <LinkButton size={option}>Link button</LinkButton>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="link-button-showcase__matrix">
            <h3 className="ui-showcase-live__title">States</h3>
            {LINK_BUTTON_PREVIEW_STATES.map((state) => (
              <div
                key={state}
                className={`link-button-showcase__row${
                  highlightedState === state
                    ? ' link-button-showcase__row--selected'
                    : ''
                }`}
              >
                <h4 className="link-button-showcase__label">
                  {LINK_BUTTON_PREVIEW_STATE_LABELS[state]}
                </h4>
                <LinkButton previewState={state}>Link button</LinkButton>
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Link button"
        sections={propertySections}
      />
    </>
  )
}

export default LinkButtonShowcase
